// libraries
import React from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import Image from '/react4xp/components/Image'
import Mask from '/react4xp/components/Mask'
import Button from '/react4xp/components/Button'
import Link from '/react4xp/components/Link'
import Balloon from '/react4xp/components/Balloon'
import AnimatedArrow from '/react4xp/components/AnimatedArrow'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

// svg
import { UxArrowRight } from '/react4xp/components/Svg/Ux/Arrow'

// prop types
interface CampaignCardProps {

    // section props
    section: {
        bgColor: 'blue' | 'white' | 'green' | string
        noSeparator?: boolean
        paddingTop?: 'small' | 'medium' | 'big' | 'none'
        paddingBottom?: 'small' | 'medium' | 'big' | 'none'
    }

    // product card props
    boxColor: 'yoghurt' | 'white' | string
    boxTextColor?: 'white'| 'black'
    imagePosition?: 'left' | 'right'
    src?: string
    srcMobile?: string
    alt?: string
    balloonText?: string
    isWhite?: boolean
    title?: string
    text?: string
    hrefStyle?: 'button' | 'cta'
    hrefText?: string
    href?: string
    external?: boolean
    className?: string
}

export default function CampaignCard({
    section,
    imagePosition,
    boxColor,
    boxTextColor,
    src,
    srcMobile,
    alt,
    balloonText,
    isWhite,
    title,
    text,
    hrefStyle,
    hrefText,
    href,
    external,
    className
}: CampaignCardProps) {
    return (
        <Section
            bgColor={section.bgColor}
            noSeparator={section.noSeparator}
            paddingTop={section.paddingTop}
            paddingBottom={section.paddingBottom}
            className='campaign-card'
        >
            <div className={clsx(
                'container relative',
                isNonWhitespaceString(src) ? '' : 'no-image'
            )}>

                {isNonWhitespaceString(balloonText) && (
                    <Balloon
                        balloonColor='blue-dark'
                        spacing='small'
                        arrowSize='small'
                        arrow='bottom-middle'
                        balloonSide={imagePosition === 'left' ? 'right' : 'left'}
                        arrowSide={imagePosition === 'left' ? 'left' : 'right'}
                        className={clsx(
                            'campaign-card__balloon',
                            imagePosition === 'left' && 'campaign-card__balloon--left',
                            imagePosition === 'right' && 'campaign-card__balloon--right',
                            isNonWhitespaceString(src) ? '' : 'campaign-card__balloon--no-image'
                        )}
                    >
                        <p className='h3 line-break'>
                            {balloonText}
                        </p>
                    </Balloon>
                )}

                <Mask
                    bgColor={boxColor}
                    spacing='medium'
                    className={clsx(
                        'campaign-card__box',
                        imagePosition === 'left' && 'campaign-card__box--left',
                        imagePosition === 'right' && 'campaign-card__box--right',
                        className
                    )}
                >

                    <div className={clsx(
                        'campaign-card__image-wrapper',
                        isNonWhitespaceString(src) ? '' : 'campaign-card__image-wrapper--no-image'
                    )}>
                        {isNonWhitespaceString(src) && (
                            <Mask className='campaign-card__image'>
                                <Image
                                    src={src}
                                    srcMedium={srcMobile}
                                    alt={alt}
                                    width={600}
                                    sizes='(max-width: 768px) 100vw, 600px'
                                    className='cover'
                                />
                            </Mask>
                        )}
                    </div>

                    <div className={clsx(
                        'campaign-card__wrapper',
                        isWhite && 'white',
                        boxTextColor === 'black' && 'black',
                        boxTextColor === 'white' && 'white'
                    )}>

                        {isNonWhitespaceString(title) && (
                            <h2 className='h3'>
                                {title}
                            </h2>
                        )}

                        {isNonWhitespaceString(text) && (
                            <div
                                className='campaign-card__wrapper__text'
                                dangerouslySetInnerHTML={{ __html: text }}
                                suppressHydrationWarning
                            />
                        )}

                        {isNonWhitespaceString(href) && (
                            hrefStyle === 'button' ? (
                                isNonWhitespaceString(hrefText) && (
                                    <Button
                                        href={href}
                                        target={!!external}
                                        hasBorders
                                        noUnderline
                                        color='transparent'
                                        hover='blue-dark'
                                    >
                                        {hrefText}
                                    </Button>
                                )
                            ) : (
                                isNonWhitespaceString(hrefText) ? (
                                    <Link
                                        text={hrefText}
                                        href={href}
                                        target={!!external}
                                        iconPosition='right'
                                        icon={<UxArrowRight />}
                                        isWhite={isWhite}
                                    />
                                ) : (
                                    <a
                                        href={href}
                                        target={external ? '_blank' : undefined}
                                        className='campaign-card__wrapper__arrow'
                                    >
                                        <AnimatedArrow isWhite={isWhite} />
                                    </a>
                                )
                            )
                        )}

                    </div>

                </Mask>
            </div>
        </Section>
    )
}